import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Transition} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/TransitionTypes';
import {getAdjacencyMapFromProcessFlowchart} from 'util/graph-utils/getFlowchartAdjacencyMap';
import getLoopIgnoringAncestorsAndEdges from 'util/graph-utils/getLoopIgnoringAncestorsAndEdges';
import getLowestCommonAncestors from 'util/graph-utils/getLowestCommonAncestors';
import {AdjacencyMap} from 'util/graph-utils/GraphUtilConstants';
import isNodeDominated from 'util/graph-utils/isNodeDominated';

import {
  ProcessFlowchartErrorType,
  ProcessFlowchartObjectType,
  ProcessFlowchartValidationError,
} from './ProcessFlowchartForValidationTypes';
import {
  ProcessNodeForValidation,
  ProcessFlowchartForValidation,
  ValidationContext,
} from './ProcessFlowchartForValidationTypes';
import {ProcessFlowchartDesignSpace} from './ProcessGraphBuilderConstants';
import {
  getFlowchartNodeFromClientId,
  getFulfillingMapperConfigs,
  getInboundEdges,
  getTaskNodesUpstreamOfNode,
  getTemplateNodeFromSchemaId,
} from './ProcessGraphGetters';

/**
 * Computes the lists of nodes that are being merged into the current node.
 *
 *
 * ┌──►B──────┐
 * │          │
 * ├──►C──────┤
 * │          └──►
 * A               G
 * │         ┌───►   ▲
 * ├──►D─────┤       │
 * │         │       │
 * ├──►E─────┘       │
 * │                 │
 * └──►F─────────────┘
 *
 * In the graph above, G has:
 * - five incoming edges (from B, C, D, E, and F)
 * - three incoming mappers (from B&C, D&E, and F)
 *
 * So this function would return [[B, C], [D, E]] because [B, C] and [D, E] represent individual merges into
 * G. F is not returned because it is not part of a merge.
 *
 * @param node The node
 * @param flowchart The flowchart
 * @param designSpace The design space
 * @returns A list of the nodes being merged into `node`.
 */
function getAllMergeSources(
  node: ProcessNodeForValidation,
  flowchart: ProcessFlowchartForValidation,
  designSpace: ProcessFlowchartDesignSpace
): Array<Array<ProcessNodeForValidation>> | null {
  // The associated template node is guaranteed to exist by ensureNodeIsInsideDesignSpace. But we
  // double-check, in case the tests are out of order
  const templateNode =
    node.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
    node.nodeType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
      ? getTemplateNodeFromSchemaId(designSpace.availableNodes, node.taskSchemaId)
      : null;
  if (templateNode == null) {
    return null;
  }

  const incomingEdges = getInboundEdges(node, flowchart.edges);
  // The node must have at least two edges to be a merge point.
  if (incomingEdges.length <= 1) {
    return null;
  }

  const fulfillingMapperConfigs = getFulfillingMapperConfigs(node, flowchart, templateNode);
  if (fulfillingMapperConfigs == null) {
    return null;
  }

  const mergingAncestorsList: Array<Array<ProcessNodeForValidation>> = [];
  for (const {fromTaskSchemaIds} of fulfillingMapperConfigs) {
    if (fromTaskSchemaIds.length > 1) {
      const mergingAncestors = [];
      for (const incomingEdge of incomingEdges) {
        const upstreamNode = getFlowchartNodeFromClientId(flowchart, incomingEdge.source);
        assert(upstreamNode != null, 'Expected edge to be connected to node', {
          flowchart,
          incomingEdge,
        });
        // Parent nodes should include the task nodes upstream of any directly upstream router node.
        // Currently this can only be one task node.
        const parentNodes =
          upstreamNode.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
          upstreamNode.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
            ? [upstreamNode]
            : getTaskNodesUpstreamOfNode(upstreamNode.id, flowchart);
        for (const parentNode of parentNodes) {
          if (
            (parentNode.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
              parentNode.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS) &&
            fromTaskSchemaIds.includes(parentNode.taskSchemaId)
          ) {
            mergingAncestors.push(parentNode);
          }
        }
      }
      mergingAncestorsList.push(mergingAncestors);
    }
  }
  return mergingAncestorsList;
}

/**
 * Computes the lists of nodes that are being merged into the current node using transitions
 * (flowchart-first config). A merge is defined by a transition whose target is this node's PTDV
 * and which has multiple source PTDV IDs.
 *
 * @param targetNode The target node (task or output)
 * @param flowchart The flowchart
 * @param transitions Transitions from the flowchart config
 * @returns A list of the nodes being merged into `node`, or null if not a merge point.
 */
function getMergeSourcesFromTransitions(
  targetNode: ProcessNodeForValidation,
  flowchart: ProcessFlowchartForValidation,
  transitions: Record<string, Transition>
): Array<Array<ProcessNodeForValidation>> | null {
  if (
    targetNode.nodeType !== ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS &&
    targetNode.nodeType !== ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
  ) {
    return null;
  }

  const targetPTDVId = targetNode.taskDefinitionVersionSpec?.clientId;
  if (targetPTDVId == null) {
    return null;
  }

  const upstreamTaskNodes = getTaskNodesUpstreamOfNode(targetNode.id, flowchart);
  const mergingAncestorsList: Array<Array<ProcessNodeForValidation>> = [];

  for (const transition of Object.values(transitions)) {
    if (transition.targetPTDVId !== targetPTDVId || transition.sourcePTDVIds.length <= 1) {
      continue;
    }
    const sourcePTDVIds = transition.sourcePTDVIds;
    const mergingAncestors = upstreamTaskNodes.filter(
      (upstreamNode) =>
        upstreamNode.taskDefinitionVersionSpec?.clientId != null &&
        sourcePTDVIds.includes(upstreamNode.taskDefinitionVersionSpec.clientId)
    );
    if (mergingAncestors.length >= 2) {
      mergingAncestorsList.push(mergingAncestors);
    }
  }

  return mergingAncestorsList.length > 0 ? mergingAncestorsList : null;
}

/**
 * Validates that all merge points (aka merging LOYSP) resolve exactly one branch point.
 * While we have all merging nodes and LCAs, we also check that router nodes do not branch out of the merge.
 *
 * In this algorithm a "branch point" is a point in the graph at which the graph "forks" or "diverges".
 * If there is a merging LOYSP we require it to "resolve" (aka merge as we look backward) exactly one branch
 * point. We also require that branch point to be the LCA of the nodes being merged in the LOYSP.
 *
 * **Example 1**
 *
 *     B
 *   /   \
 * A       D
 *   \   /
 *     C
 *
 * In the graph above, A is a "branch point" because the graph is diverging at this point. D is a "merge point"
 * where B and C are being merged into D. The LCA of B and C is A and we can visually see that A is the only
 * branch point being resolved by merging B and C into D. Since the merge point at D resolves only one branch point
 * which is the LCA of the merging nodes, this graph is valid.
 *
 * **Example 2**
 *
 *    B --> C
 *  /  \  /   \
 * A     /      F
 *  \   /  \   /
 *    D -->  E
 *
 * In the graph above, F is a merge point that is merging C and E. But the branch point of C and E is ambiguous.
 * Both B and D are the LCA of C and E. Since the branch point is ambiguous, this graph is invalid.
 *
 * **Example 3**
 *
 *     B --> C
 *   /     /   \
 * A      /     F
 *   \   /     /
 *     D --> E
 *
 * In the graph above, F is the merge point that is merging C and E. However, we can see that while D is the
 * branch point (LCA of C and E), C also depends on B which does not descend from D. This means that the merge
 * of C, E is attempting to resolve two branch points - D and A. This can lead to nonintuitive behavior because
 * of how we are pooling inputs to the merge nodes. It will leave any tasks at C which did not originate from D
 * "orphaned" (eg without a sibling at E) and therefore unmapped. Because of this ambiguity, thsi graph is invalid.
 *
 * So we want to ensure that any merge point has exactly one branch point. This means that
 * 1. All merge points need to have one branch point.
 * 2. Any nodes that are merging into a merge point must be dominated by their least common ancestor.
 *    See: https://en.wikipedia.org/wiki/Dominator_(graph_theory)#Algorithms
 *
 * We are detecting these graph topologies using the following algorithm:
 *
 * For each merge point:
 * 1. Find the nearest branch point. If there are multiple branch points, the graph is invalid.
 * 2. Find all nodes that are ancestors of the merging nodes but are not ancestors of the branch point.
 *    This set represents that nodes that are the dependencies for the merge - in example 3 this would return
 *    B, C, D, and E.
 * 3. Validate that every node from (2) descends from the branch point. If any such node does not descend from
 *    the branch point we raise an error.
 * 4. Validate that for every merging node, there is no router node between the node and the LCA that branches
 *    out of the merge. Also validate that there are no router nodes between the merge point and merging node.
 *
 * @param flowchart The flowchart
 * @param designSpace The design space
 * @returns A list of error objects
 */
export default function ensureMergingNodesOnlyDependOnOneBranchPoint(
  flowchart: ProcessFlowchartForValidation,
  designSpace: ProcessFlowchartDesignSpace,
  options?: {
    validationContext?: ValidationContext;
    usingFlowchartFirstConfig?: boolean;
  }
): Array<ProcessFlowchartValidationError> | null {
  const {validationContext, usingFlowchartFirstConfig} = options ?? {};
  const transitions = validationContext?.transitions;
  const shouldUseTransitions = usingFlowchartFirstConfig === true && transitions != null;

  const errors: Array<ProcessFlowchartValidationError> = [];
  const rootNode = flowchart.nodes.find(
    (node) => node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
  );
  if (rootNode == null) {
    // If the root node doesn't exist we will raise a validation error elsewhere.
    return null;
  }
  const adjacencyMap: AdjacencyMap = getAdjacencyMapFromProcessFlowchart(flowchart);

  const {nodeIdsToAncestors, adjacencyMapLoopsRemoved} = getLoopIgnoringAncestorsAndEdges(
    rootNode.id,
    adjacencyMap
  );
  for (const node of flowchart.nodes) {
    if (node.nodeType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS) {
      continue;
    }
    const mergingSourceNodesList = shouldUseTransitions
      ? getMergeSourcesFromTransitions(node, flowchart, transitions)
      : getAllMergeSources(node, flowchart, designSpace);
    if (mergingSourceNodesList == null) {
      continue;
    }
    const ambiguousBranchPointError = {
      object: {
        clientId: node.id,
        type: ProcessFlowchartObjectType.NODE,
      },
      errorMessage: `${node.name} can't merge paths from different branching points. Create a separate merge for each branch.`,
      errorType: ProcessFlowchartErrorType.MERGE_POINT_WITH_AN_AMBIGUOUS_BRANCH_POINT,
      blocksPersistence: true,
    };
    for (const mergingNodes of mergingSourceNodesList) {
      const mergingLoyspLCAs = getLowestCommonAncestors(
        mergingNodes.map(({id}) => id),
        nodeIdsToAncestors,
        adjacencyMapLoopsRemoved
      );
      if (mergingLoyspLCAs.length !== 1) {
        errors.push(ambiguousBranchPointError);
        continue;
      }
      const mergingLoyspLCA = mergingLoyspLCAs[0];
      for (const mergingNode of mergingNodes) {
        if (!isNodeDominated(mergingNode.id, mergingLoyspLCA, rootNode.id, adjacencyMap)) {
          errors.push(ambiguousBranchPointError);
        }
        const routerNodesBetweenMergingNodeAndLCA = [...nodeIdsToAncestors[mergingNode.id]].filter(
          (ancestorNodeClientId) => {
            const ancestorNode = getFlowchartNodeFromClientId(flowchart, ancestorNodeClientId);
            return (
              // Check that router is descendant of LCA
              ancestorNode != null &&
              ancestorNode.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS &&
              nodeIdsToAncestors[ancestorNodeClientId].has(mergingLoyspLCA)
            );
          }
        );
        const routerNodesBetweenMergePointAndMergingNode = [...nodeIdsToAncestors[node.id]].filter(
          (ancestorNodeClientId) => {
            const ancestorNode = getFlowchartNodeFromClientId(flowchart, ancestorNodeClientId);
            return (
              // Check that router is descendant of merging node
              ancestorNode != null &&
              ancestorNode.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS &&
              nodeIdsToAncestors[ancestorNodeClientId].has(mergingNode.id)
            );
          }
        );
        const routerNodeError = (routerNodeName: string): ProcessFlowchartValidationError => ({
          object: {
            clientId: node.id,
            type: ProcessFlowchartObjectType.NODE,
          },
          errorMessage: `Tasks in ${node.name} can't be created because ${routerNodeName} may send some tasks to other paths. Update the flowchart or the transitions to ${node.name}.`,
          errorType: ProcessFlowchartErrorType.MERGE_POINT_WITH_AN_AMBIGUOUS_BRANCH_POINT,
          blocksPersistence: true,
        });
        for (const routerNodeClientId of routerNodesBetweenMergingNodeAndLCA) {
          const downstreamNodeClientIds = adjacencyMap[routerNodeClientId];
          const routerNode = getFlowchartNodeFromClientId(flowchart, routerNodeClientId);
          assert(routerNode != null, 'Expected router node to be found');
          // There is an error if a node directly downstream of the router leaves the merge.
          // We check this by pushing an error if the downstream node is not an ancestor of mergingNode.
          // We use the merging node rather than the merge point to catch the case where the downstream node leaves the merge but still merges into the merge point.
          if (
            downstreamNodeClientIds.some(
              (downstreamNodeClientId) => !nodeIdsToAncestors[mergingNode.id].has(downstreamNodeClientId)
            )
          ) {
            errors.push(routerNodeError(routerNode.name));
          }
        }
        for (const routerNodeClientId of routerNodesBetweenMergePointAndMergingNode) {
          // For any router nodes between the merge point and merging node, we want to error.
          // We check this separately, as it's not caught by the LCA - merging node check above.
          const routerNode = getFlowchartNodeFromClientId(flowchart, routerNodeClientId);
          assert(routerNode != null, 'Expected router node to be found');
          errors.push(routerNodeError(routerNode.name));
        }
      }
    }
  }

  return errors;
}
